import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';

import {
	createIncomingLinksSsrState,
	INCOMING_LINKS_SAMPLE_LIMIT,
	type IncomingLinksSampleResult,
	type IncomingLinksSsrState,
} from './incomingLinksSsrState.ts';
import { preloadIncomingLinksSample } from './preloadIncomingLinksSample.ts';

type IncomingLinksSsrRoute = 'view' | 'live';

type PreloadIncomingLinksSsrStateArgs = Readonly<{
	cloudId: string;
	contentId: string;
	route: IncomingLinksSsrRoute;
}>;

const INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE = 'ssr-app/prepare/incomingLinksSample';

const recordIncomingLinksSampleOutcome = (
	result: IncomingLinksSampleResult,
	route: IncomingLinksSsrRoute,
): void => {
	let outcome: string;
	if (result.status === 'unavailable') {
		outcome = result.reason;
	} else if (result.count === 0) {
		outcome = 'empty';
	} else if (result.count >= INCOMING_LINKS_SAMPLE_LIMIT) {
		outcome = 'limit-reached';
	} else {
		outcome = 'one-or-two';
	}

	SSRMeasures.addMeasure(`${INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:${outcome}`, 0);
};

const createStateFromSample = (
	contentId: string,
	result: IncomingLinksSampleResult,
): IncomingLinksSsrState => {
	if (result.status === 'unavailable') {
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}

	return createIncomingLinksSsrState(contentId, result.count > 0 ? 'present' : 'empty');
};

export const preloadIncomingLinksSsrState = async ({
	cloudId,
	contentId,
	route,
}: PreloadIncomingLinksSsrStateArgs): Promise<IncomingLinksSsrState> => {
	let samplePromise: Promise<IncomingLinksSampleResult> | undefined;
	const getSample = (): Promise<IncomingLinksSampleResult> => {
		samplePromise ??= preloadIncomingLinksSample({ cloudId, contentId });
		return samplePromise;
	};
	const completedWithinGuard = await slowQueryGuard(
		'preloadIncomingLinksSample',
		async () => {
			await getSample();
		},
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);

	if (!completedWithinGuard) {
		SSRMeasures.addMeasure(`${INCOMING_LINKS_SAMPLE_OUTCOME_MEASURE}/${route}/outcome:timeout`, 0);
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}

	const sample = await getSample();
	recordIncomingLinksSampleOutcome(sample, route);
	return createStateFromSample(contentId, sample);
};
